import { useState, useEffect, useContext } from "react";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import { Card, Spin, Select, Table, Tag, Row, Col, Segmented, Button } from "antd";
import {
  DownloadOutlined,
  MinusCircleOutlined,
} from "@ant-design/icons";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, Cell,
} from "recharts";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const { Option } = Select;

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];
const now = new Date();
const CURRENT_MONTH = now.getMonth() + 1;
const CURRENT_YEAR  = now.getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - 2 + i);

const fmtCurrency = (v) =>
  `₹ ${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const DEDUCTION_KEYS = [
  { key: "pf",               label: "PF",       color: "#6366f1" },
  { key: "esi",              label: "ESI",      color: "#3b82f6" },
  { key: "pt",               label: "PT",       color: "#0891b2" },
  { key: "lop_deduction",    label: "NLB",      color: "#bf4406" },
  { key: "advances",         label: "Advances", color: "#ef4444" },
  { key: "total_deductions", label: "Total",    color: "#0f172a" },
];

export default function LopChart() {
  const { backUrl, authData } = useContext(AuthContext);

  const [month,        setMonth]        = useState(CURRENT_MONTH);
  const [year,         setYear]         = useState(CURRENT_YEAR);
  const [loading,      setLoading]      = useState(false);
  const [organisations, setOrganisations] = useState([]);
  const [orgId,        setOrgId]        = useState("");
  const [data,         setData]         = useState(null);
  const [chartView,    setChartView]    = useState("dept");   // "dept" | "type"
  const [deductionKey, setDeductionKey] = useState("total_deductions");

  /* fetch organisations */
  useEffect(() => {
    axios.get(`${backUrl}/api/emp/organisations/`, {
      headers: { Authorization: `Bearer ${authData?.access}` },
    })
    .then(r => {
      const orgs = r.data.results || r.data || [];
      setOrganisations(orgs);
      if (orgs.length > 0) setOrgId(String(orgs[0].id));
    })
    .catch(() => {});
  }, [backUrl, authData?.access]);

  /* fetch deductions */
  useEffect(() => {
    setLoading(true);
    const orgParam = orgId && orgId !== "ALL" ? `&organisation=${orgId}` : "";
    axios.get(`${backUrl}/api/reports/payroll-deductions/?month=${month}&year=${year}${orgParam}`, {
      headers: { Authorization: `Bearer ${authData?.access}` },
    })
    .then(r => setData(r.data))
    .catch(() => setData(null))
    .finally(() => setLoading(false));
  }, [month, year, orgId, backUrl, authData?.access]);

  const employees = data?.employees || [];
  const summary   = data?.summary   || {};

  /* department-wise grouping */
  const deptChart = () => {
    const groups = {};
    employees.forEach(e => {
      const d = e.department || "Unknown";
      if (!groups[d]) groups[d] = {};
      DEDUCTION_KEYS.forEach(({ key }) => {
        groups[d][key] = (groups[d][key] || 0) + Number(e[key] || 0);
      });
    });
    return Object.entries(groups)
      .map(([dept, vals]) => ({ dept: dept.length > 12 ? dept.slice(0, 12) + "…" : dept, ...vals }))
      .sort((a, b) => b.total_deductions - a.total_deductions);
  };

  /* deduction-type summary */
  const typeChart = [
    { name: "PF",       value: Number(summary.total_pf      || 0), color: "#6366f1" },
    { name: "ESI",      value: Number(summary.total_esi     || 0), color: "#3b82f6" },
    { name: "PT",       value: Number(summary.total_pt      || 0), color: "#0891b2" },
    { name: "NLB",      value: Number(summary.total_lop_deduction || 0), color: "#bf4406" },
    { name: "Advances", value: Number(summary.total_advances|| 0), color: "#ef4444" },
  ].filter(d => d.value > 0);

  /* excel download */
  const downloadExcel = () => {
    if (!employees.length) return;
    const ws = XLSX.utils.json_to_sheet(employees);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Deductions");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      `deductions_${month}_${year}.xlsx`);
  };

  /* table columns */
  const columns = [
    {
      title: "#", key: "idx", width: 44,
      render: (_, __, i) => <span style={{ color: "#94a3b8", fontSize: 12, fontWeight: 700 }}>{i + 1}</span>,
    },
    {
      title: "Code", dataIndex: "employee_code", width: 80,
      render: v => <Tag style={{ borderRadius: 6, fontWeight: 700, fontSize: 11 }}>{v}</Tag>,
    },
    {
      title: "Employee", dataIndex: "employee_name",
      render: (name, rec) => (
        <div>
          <div style={{ fontWeight: 700, color: "#0f172a", fontSize: 13 }}>{name}</div>
          <div style={{ fontSize: 11, color: "#94a3b8" }}>{rec.designation} · {rec.department}</div>
        </div>
      ),
    },
    { title: "PF",    dataIndex: "pf",            align: "right", width: 80,  render: v => <span style={{ color: "#6366f1", fontWeight: 700 }}>{fmtCurrency(v)}</span> },
    { title: "ESI",   dataIndex: "esi",           align: "right", width: 80,  render: v => <span style={{ color: "#3b82f6", fontWeight: 700 }}>{fmtCurrency(v)}</span> },
    { title: "PT",    dataIndex: "pt",            align: "right", width: 70,  render: v => <span style={{ color: "#0891b2", fontWeight: 700 }}>{fmtCurrency(v)}</span> },
    { title: "LOP",   dataIndex: "lop_deduction", align: "right", width: 80,  render: v => <span style={{ color: "#f59e0b", fontWeight: 700 }}>{fmtCurrency(v)}</span> },
    { title: "Advances", dataIndex: "advances",   align: "right", width: 90,  render: v => <span style={{ color: "#ef4444", fontWeight: 700 }}>{fmtCurrency(v)}</span> },
    {
      title: "Total Deductions", dataIndex: "total_deductions", align: "right", width: 120,
      render: v => <span style={{ fontWeight: 900, color: "#0f172a", fontSize: 13 }}>{fmtCurrency(v)}</span>,
      sorter: (a, b) => a.total_deductions - b.total_deductions,
      defaultSortOrder: "descend",
    },
  ];

  return (
    <Card
      variant="borderless"
      style={{ borderRadius: 16, border: "1px solid #e2e8f0", boxShadow: "0 2px 16px rgba(15,23,42,0.05)" }}
      styles={{ body: { padding: "18px 20px" } }}
    >
      {/* ── Header ── */}
      <Row justify="space-between" align="middle" gutter={[0, 10]} style={{ marginBottom: 18 }}>
        <Col xs={24} md={10}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 38, height: 38, borderRadius: 10,
              background: "linear-gradient(135deg,#f59e0b,#d97706)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <MinusCircleOutlined style={{ color: "#fff", fontSize: 18 }} />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 15, color: "#0f172a" }}>
                NLB & Salary Deductions
              </div>
              <div style={{ fontSize: 12, color: "#64748b" }}>
                PF · ESI · PT · NLB · Advances · {MONTHS[month - 1]} {year}
              </div>
            </div>
          </div>
        </Col>
        <Col xs={24} md={14}>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
            <Select
              value={orgId || "ALL"}
              onChange={(v) => setOrgId(v || "ALL")}
              allowClear
              onClear={() => setOrgId("ALL")}
              style={{ width: 170 }}
              size="small"
              placeholder="Organisation"
            >
              <Option value="ALL">All Organisations ({organisations.length})</Option>
              {organisations.map(o => <Option key={o.id} value={String(o.id)}>{o.name}</Option>)}
            </Select>
            <Select value={month} onChange={setMonth} style={{ width: 110 }} size="small">
              {MONTHS.map((m, i) => <Option key={i + 1} value={i + 1}>{m}</Option>)}
            </Select>
            <Select value={year} onChange={setYear} style={{ width: 82 }} size="small">
              {YEARS.map(y => <Option key={y} value={y}>{y}</Option>)}
            </Select>
            <Button size="small" icon={<DownloadOutlined />} onClick={downloadExcel} disabled={!employees.length}>
              Export
            </Button>
          </div>
        </Col>
      </Row>

      {loading ? (
        <div style={{ textAlign: "center", padding: "60px 0" }}>
          <Spin size="large" />
          <div style={{ marginTop: 12, color: "#64748b", fontWeight: 600 }}>Loading deduction data…</div>
        </div>
      ) : (
        <>
          {/* ── KPI Summary Strip ── */}
          <Row gutter={[10, 10]} style={{ marginBottom: 20 }}>
            {[
              { label: "Total PF",       val: summary.total_pf,             color: "#6366f1", bg: "#eef2ff", border: "#c7d2fe" },
              { label: "Total ESI",      val: summary.total_esi,            color: "#3b82f6", bg: "#eff6ff", border: "#bfdbfe" },
              { label: "Total PT",       val: summary.total_pt,             color: "#0891b2", bg: "#ecfeff", border: "#a5f3fc" },
              { label: "Total LOP",      val: summary.total_lop_deduction,  color: "#f59e0b", bg: "#fffbeb", border: "#fde68a" },
              { label: "Total Advances", val: summary.total_advances,       color: "#ef4444", bg: "#fff1f2", border: "#fecaca" },
              { label: "Total Deductions", val: summary.total_deductions,   color: "#0f172a", bg: "#f8fafc", border: "#e2e8f0" },
            ].map(k => (
              <Col xs={12} sm={8} md={4} key={k.label}>
                <div style={{
                  background: k.bg, border: `1px solid ${k.border}`,
                  borderRadius: 12, padding: "10px 12px", textAlign: "center",
                }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: k.color, textTransform: "uppercase", marginBottom: 4 }}>
                    {k.label}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 900, color: k.color }}>
                    ₹{Number(k.val || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                  </div>
                </div>
              </Col>
            ))}
          </Row>

          {/* ── Chart ── */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#334155" }}>
              Deduction Analysis
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Segmented
                size="small"
                value={chartView}
                onChange={setChartView}
                options={[
                  { label: "By Department", value: "dept" },
                  { label: "By Type",       value: "type" },
                ]}
              />
              {chartView === "dept" && (
                <Select value={deductionKey} onChange={setDeductionKey} size="small" style={{ width: 110 }}>
                  {DEDUCTION_KEYS.map(d => <Option key={d.key} value={d.key}>{d.label}</Option>)}
                </Select>
              )}
            </div>
          </div>

          <ResponsiveContainer width="100%" height={220}>
            {chartView === "dept" ? (
              <BarChart data={deptChart()} barSize={20} margin={{ top: 0, right: 16, bottom: 0, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="dept" tick={{ fontSize: 10, fill: "#64748b" }} />
                <YAxis tick={{ fontSize: 10, fill: "#64748b" }} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={v => fmtCurrency(v)} />
                <Bar
                  dataKey={deductionKey}
                  fill={DEDUCTION_KEYS.find(d => d.key === deductionKey)?.color || "#6366f1"}
                  radius={[4, 4, 0, 0]}
                  name={DEDUCTION_KEYS.find(d => d.key === deductionKey)?.label}
                />
              </BarChart>
            ) : (
              <BarChart data={typeChart} barSize={40} layout="vertical" margin={{ top: 0, right: 40, bottom: 0, left: 50 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10, fill: "#64748b" }} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "#334155", fontWeight: 600 }} />
                <Tooltip formatter={v => fmtCurrency(v)} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]} name="Amount">
                  {typeChart.map((entry, index) => (
                    <Cell key={index} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            )}
          </ResponsiveContainer>

          {/* ── Employee Table ── */}
          <div style={{ fontSize: 13, fontWeight: 700, color: "#334155", margin: "18px 0 10px" }}>
            Employee-wise Deduction Details
          </div>
          <Table
            dataSource={employees}
            columns={columns}
            rowKey="id"
            size="small"
            pagination={{ pageSize: 8, showSizeChanger: false }}
            scroll={{ x: 780 }}
            locale={{ emptyText: <div style={{ padding: "32px 0", color: "#94a3b8", textAlign: "center" }}>No deduction records for this period.</div> }}
            style={{ borderRadius: 10, overflow: "hidden", border: "1px solid #e2e8f0" }}
          />
        </>
      )}
    </Card>
  );
}
